import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Settings, Play, Loader2 } from 'lucide-react';
import { useDatabaseContext } from '../contexts/DatabaseContext';
import OptimizationService from '../services/optimizationService';
import {
  OptimizationRunParams,
  PredictionModel
} from '../types/optimization';

interface OptimizationFormProps {
  onOptimizationStart: (runId: string) => void;
  onOptimizationComplete: (results: any) => void;
  onError: (error: string) => void;
  isRunning: boolean;
}

const OptimizationForm: React.FC<OptimizationFormProps> = ({
  onOptimizationStart,
  onOptimizationComplete,
  onError,
  isRunning
}) => {
  const { activeDatabase } = useDatabaseContext();
  const [params, setParams] = useState<OptimizationRunParams>({
    target_month: new Date().toISOString().slice(0, 10),
    optimization_horizon_months: 10,
    min_fill_rate: 0.9,
    default_coverage_target: 16,
    new_article_coverage: 12,
    terminated_article_coverage: 0
  });

  const [availableModels, setAvailableModels] = useState<PredictionModel[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadAvailableModels = useCallback(async () => {
    if (!activeDatabase) return;
    
    setLoadingModels(true);
    try {
      const models = await OptimizationService.getAvailableModels(activeDatabase.id);
      setAvailableModels(models);
      if (models.length > 0) {
        setParams(prev => ({ ...prev, model_name: models[0].name }));
      }
    } catch (error) {
      console.error('Error loading models:', error);
      onError('Erreur lors du chargement des modèles de prédiction');
    } finally {
      setLoadingModels(false);
    }
  }, [activeDatabase, onError]);

  useEffect(() => {
    if (activeDatabase) {
      loadAvailableModels();
    }
  }, [activeDatabase, loadAvailableModels]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!activeDatabase) {
      onError('Aucune base de données sélectionnée');
      return;
    }

    if (isSubmitting || isRunning) {
      return; // Guard against double submit
    }

    try {
      setIsSubmitting(true);
      const result = await OptimizationService.runOptimization(
        activeDatabase.id,
        params
      );
      
      onOptimizationStart(result.optimization_run_id);
      
      if (result.status === 'completed') {
        onOptimizationComplete(result);
      }
    } catch (error: any) {
      console.error('Error running optimization:', error);
      onError(
        error.response?.data?.error || 
        'Erreur lors du lancement de l\'optimisation'
      );
    }
    finally {
      // Keep the button disabled only until parent flips isRunning to true
      setIsSubmitting(false);
    }
  };

  const handleParamChange = (key: keyof OptimizationRunParams, value: any) => {
    setParams(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center gap-3 mb-6">
        <Settings className="text-blue-600" size={24} />
        <h2 className="text-xl font-semibold text-gray-900">
          Configuration d'Optimisation
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Primary Parameters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Target Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="inline w-4 h-4 mr-1" />
              Date cible
            </label>
            <input
              type="date"
              value={params.target_month}
              onChange={(e) => handleParamChange('target_month', e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Date de départ pour l'optimisation
            </p>
          </div>

          {/* Prediction Model */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Modèle de prédiction
            </label>
            {loadingModels ? (
              <div className="flex items-center p-3 border border-gray-300 rounded-md">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Chargement des modèles...
              </div>
            ) : (
              <select
                value={params.model_name || ''}
                onChange={(e) => handleParamChange('model_name', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Sélectionner un modèle</option>
                {availableModels.map((model) => (
                  <option key={model.name} value={model.name}>
                    {model.display_name || model.name}
                  </option>
                ))}
              </select>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Modèle utilisé pour les prévisions de vente
            </p>
          </div>
        </div>

        {/* Advanced Parameters Toggle */}
        <div>
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            {showAdvanced ? 'Masquer' : 'Afficher'} les paramètres avancés
          </button>
        </div>

        {/* Advanced Parameters */}
        {showAdvanced && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Horizon d'optimisation (mois)
              </label>
              <input
                type="number"
                min="1"
                max="24"
                value={params.optimization_horizon_months}
                onChange={(e) => handleParamChange('optimization_horizon_months', parseInt(e.target.value))}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Taux de remplissage min. (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                value={(params.min_fill_rate || 0.9) * 100}
                onChange={(e) => handleParamChange('min_fill_rate', parseFloat(e.target.value) / 100)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Couverture par défaut (mois)
              </label>
              <input
                type="number"
                min="1"
                max="36"
                value={params.default_coverage_target}
                onChange={(e) => handleParamChange('default_coverage_target', parseInt(e.target.value))}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Couverture nouveaux articles (mois)
              </label>
              <input
                type="number"
                min="0"
                max="36"
                value={params.new_article_coverage}
                onChange={(e) => handleParamChange('new_article_coverage', parseInt(e.target.value))}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Couverture articles arrêtés (mois)
              </label>
              <input
                type="number"
                min="0"
                max="12"
                value={params.terminated_article_coverage}
                onChange={(e) => handleParamChange('terminated_article_coverage', parseInt(e.target.value))}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isRunning || isSubmitting || !activeDatabase || loadingModels}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {(isRunning || isSubmitting) ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Optimisation en cours...
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                Lancer l'optimisation
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OptimizationForm;
